import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    OutlinedInput,
    Select,
    TextField,
    Typography
} from "@mui/material"
import { useEffect, useState } from "react"
import DataGridTable from "./common/DataGridTable"
import DebugService from "../services/DebugService"
import TableLoader from "./common/contentloader/TableLoader"
import NewTable from "./common/Table.jsx"
import KpiRow from "./optimization/KpiRow.jsx"
import UserManagementApiService from "../services/UserManagementApiService"
import { LoadingButton } from "@mui/lab"

import {
    MdOutlinePersonAdd,
    MdEdit,
    MdSecurity
} from "react-icons/md";
import {
    BLOCK_CONTAINER_ICON,
    CONST_SITE_LABEL,
    STORAGE_KEYS,
    USER_ROLE,
    validateEmail,
    validatePassword,
    setScopedItem
} from "./common/Helper"
import SnackbarNotification, { SNACKBAR_THEME } from "./common/SnackbarNotification"
import APIConstants from "../services/APIConstants"
import ToggleButton from 'react-toggle-button'
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { BlockContainer } from "./common/BlockContainer"
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage"
import { useAuth } from "../state/AuthContext"
import { useNavigate } from 'react-router-dom';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';

const CustomUserTextField = (props) => {
    return (
        <TextField
            autoComplete="off"
            required
            fullWidth
            size="small"
            error={![null, undefined].includes(props['errorKey'])}
            helperText={props['errorKey']}
            {...props}
        />
    )
}

/**
 * Add/ Edit User Modal
 * @param {*} props 
 * @returns 
 */
export const AddUserModal = (props) => {
    const [isOpen, setIsOpen] = useState(props['isOpen'] ?? false)
    const [userCase, setUserCase] = useState(props['userCase'] ?? 'ADD')
    const [userData, setUserData] = useState(props['editUserData'] ?? null)
    const [isSaveDisabled, setIsSaveDisabled] = useState(true)
    const [showPassword, setShowPassword] = useState(false)
    const [errorMessages, setErrorMessages] = useState(null)
    const [userForm, setUserForm] = useState({
        email: '',
        fname: '',
        lname: '',
        password: '',
        role: USER_ROLE.READ_ONLY,
        accountIds: []
    })
    const { accountList, selectedPlatform } = useAuth()

    console.log('account list', accountList)

    /**
     * Modal close handler
     */
    const closeHandler = () => {
        props['isClose'](true)
        setIsOpen(false)
        setIsSaveDisabled(true)
        setErrorMessages(null)
    }

    /**
     * Modal save click handler
     */
    const onSaveClick = () => {
        setErrorMessages(null)
        let isValid = true

        if (!validateEmail(userForm['email'])) {
            setErrorMessages(prev => {
                return {
                    ...prev,
                    email: 'Incorrect email'
                }
            })

            isValid = false
        }

        // Validate password while adding a new user
        if (userForm['password'] && !validatePassword(userForm['password'])) {
            setErrorMessages(prev => {
                return {
                    ...prev,
                    password: 'Incorrect password'
                }
            })

            isValid = false
        }

        if (isValid) {
            props['saveClickHandler'](userForm)
        }
        // closeHandler()
    }

    /**
     * call useEffect on user form change
     */
    useEffect(() => {
        DebugService.log(`Add User Modal: user form`, userForm)

        if (
            userForm['email'] !== '' &&
            userForm['fname'] !== '' &&
            userForm['lname'] !== '' &&
            userForm['password'] !== '' &&
            userForm['role'] !== '' &&
            Array.isArray(userForm['accountIds']) &&
            userForm['accountIds'].length > 0
        ) {

            setIsSaveDisabled(false)
        } else {
            setIsSaveDisabled(true)
        }
    }, [userForm])

    /**
     * call useEffect on component load
     */
    useEffect(() => {
        if (userCase === 'EDIT' && userData !== null) {
            const accountsFromUser = Array.isArray(userData['accountIds'])
                ? userData['accountIds']
                : Array.isArray(userData['userAccounts'])
                    ? userData['userAccounts'].map(a => a['accountId'])
                    : []
        
            setUserForm(prev => ({
                ...prev,
                ...userData,
                accountIds: accountsFromUser
            }))
        }
    }, [])

    return (
        <div>
            <Modal
                keepMounted
                open={isOpen}
                onClose={closeHandler}
                aria-labelledby="keep-mounted-modal-title"
                aria-describedby="keep-mounted-modal-description"
            >
                <Box className="add-user-modal-box">
                    <Box className="add-user-modal-header">
                        <Box>
                            <Typography
                                id="keep-mounted-modal-title"
                                variant="h6"
                                className="add-user-modal-title"
                            >
                                {userCase === 'EDIT' ? 'Edit User' : 'Add User'}
                            </Typography>
                            <Typography
                                variant="body2"
                                className="add-user-modal-subtitle"
                            >
                                {userCase === 'EDIT'
                                    ? 'Update user details and role.'
                                    : 'Create a new user and assign a role.'}
                            </Typography>
                        </Box>
                        <Box className={`add-user-modal-badge ${userCase === 'EDIT' ? 'edit' : 'add'}`}>
                            {userCase === 'EDIT' ? 'Edit' : 'New'}
                        </Box>
                    </Box>

                    <Box className="add-user-modal-body">
                        <Grid
                            container
                            spacing={1}
                            className="add-user-modal-grid"
                        >
                            <Grid item xs={12} md={12}>
                                <CustomUserTextField
                                    label="Email"
                                    errorKey={errorMessages && errorMessages['email']}
                                    value={userForm['email']}
                                    onChange={(e) => setUserForm(prev => {
                                        return {
                                            ...prev,
                                            email: e['target']['value']
                                        }
                                    })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomUserTextField
                                    label="First Name"
                                    onChange={(e) => setUserForm(prev => {
                                        return {
                                            ...prev,
                                            fname: e['target']['value']
                                        }
                                    })}
                                    value={userForm['fname']}
                                    errorKey={errorMessages && errorMessages['fname']}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomUserTextField
                                    label="Last Name"
                                    onChange={(e) => setUserForm(prev => {
                                        return {
                                            ...prev,
                                            lname: e['target']['value']
                                        }
                                    })}
                                    value={userForm['lname']}
                                    errorKey={errorMessages && errorMessages['lname']}
                                />
                            </Grid>
                            {/* Show password field only when adding a new user */}
                            <Grid item xs={12} md={12}>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    required
                                    error={errorMessages && errorMessages['password'] !== undefined}
                                >
                                    <InputLabel id="demo-simple-select-label">Password</InputLabel>
                                    <OutlinedInput
                                        autoComplete="off"
                                        labelId="demo-simple-select-label"
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(prev => !prev)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        onChange={(e) => setUserForm(prev => {
                                            return {
                                                ...prev,
                                                password: e['target']['value']
                                            }
                                        })}
                                    />
                                    <FormHelperText>{errorMessages && errorMessages['password']}</FormHelperText>
                                </FormControl>

                                {/* <TextField
                                    required
                                    fullWidth
                                    size="small"
                                    label="Password"
                                    type="password"
                                    onChange={(e) => setUserForm(prev => {
                                        return {
                                            ...prev,
                                            password: e['target']['value']
                                        }
                                    })}
                                    error={errorMessages && errorMessages['password'] !== undefined}
                                    helperText={errorMessages && errorMessages['password']}
                                /> */}
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    required
                                    error={errorMessages && errorMessages['role'] !== undefined}
                                >
                                    <InputLabel id="demo-simple-select-label">Role</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={userForm['role']}
                                        label="Role"
                                        onChange={(e) => setUserForm(prev => {
                                            return {
                                                ...prev,
                                                role: e['target']['value']
                                            }
                                        })}
                                    >
                                        {Object.values(USER_ROLE)
                                            .filter(r => ![USER_ROLE.OWNER, USER_ROLE.SYSTEM_OWNER].includes(r))
                                            .map((u, i) => (
                                                <MenuItem key={i} value={u}>{u}</MenuItem>
                                            ))}
                                    </Select>
                                    <FormHelperText>{errorMessages && errorMessages['role']}</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel id="account-select-label">Accounts</InputLabel>
                                    <Select
                                        labelId="account-select-label"
                                        id="account-select"
                                        label="Accounts"
                                        multiple
                                        value={userForm['accountIds']}
                                        onChange={(e) => setUserForm(prev => {
                                            const value = e.target.value
                                            return {
                                                ...prev,
                                                accountIds: Array.isArray(value) ? value : []
                                            }
                                        })}
                                        renderValue={(selected) =>
                                            (selected || []).join(', ')
                                        }
                                    >
                                        {(accountList || [])
                                            // .filter(acc => !selectedPlatform || acc?.platformEnum === selectedPlatform)
                                            .map((acc, idx) => (
                                            <MenuItem key={idx} value={acc['accountId']}>
                                                {acc['accountId']}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                            </Grid>
                        </Grid>
                    </Box>

                    <Grid
                        container
                        spacing={1}
                        className="add-user-modal-actions"
                    >
                        <Grid item xs={12} md={12}>
                            <Box className="add-user-modal-actions-inner">
                                <Button
                                    variant='outlined'
                                    onClick={() => closeHandler()}
                                    className="add-user-modal-cancel-btn"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={isSaveDisabled}
                                    variant='contained'
                                    type="submit"
                                    onClick={() => onSaveClick()}
                                    className="add-user-modal-save-btn"
                                >
                                    Save
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                </Box>
            </Modal>
        </div>
    )
}

/**
 * Manage Permission Modal
 * @param {*} props
 * @returns
 */
const ManagePermissionModal = (props) => {
    const [isOpen, setIsOpen] = useState(props['isOpen'] ?? false)
    const [tagKey, setTagKey] = useState('')
    const [tagValues, setTagValues] = useState([])
    const [isSaveDisabled, setIsSaveDisabled] = useState(true)

    const tagKeyOptions = props['tagKeyOptions'] ?? []
    const tagValueOptions = props['tagValueOptions'] ?? []

    const closeHandler = () => {
        setIsOpen(false)
        setIsSaveDisabled(true)
        setTagKey('')
        setTagValues([])
        props['onClose'] && props['onClose']()
    }

    const onSaveClick = () => {
        if (props['onSave']) {
            props['onSave']({
                user: props['user'],
                tagKey,
                tagValues
            })
        }
        closeHandler()
    }

    useEffect(() => {
        if (tagKey !== '' && Array.isArray(tagValues) && tagValues.length > 0) {
            setIsSaveDisabled(false)
        } else {
            setIsSaveDisabled(true)
        }
    }, [tagKey, tagValues])

    return (
        <Modal
            keepMounted
            open={isOpen}
            onClose={closeHandler}
            aria-labelledby="manage-permission-modal-title"
        >
            <Box className="add-user-modal-box">
                <Box className="add-user-modal-header">
                    <Box>
                        <Typography
                            id="manage-permission-modal-title"
                            variant="h6"
                            className="add-user-modal-title"
                        >
                            Manage Permissions
                        </Typography>
                        {props['user'] && (
                            <Typography
                                variant="body2"
                                className="add-user-modal-subtitle"
                            >
                                {props['user']['email']}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box className="add-user-modal-body">
                    <Grid
                        container
                        spacing={1}
                        className="add-user-modal-grid"
                    >
                        <Grid item xs={12} md={12}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel id="tag-key-select-label">Tag Key</InputLabel>
                                <Select
                                    labelId="tag-key-select-label"
                                    id="tag-key-select"
                                    label="Tag Key"
                                    value={tagKey}
                                    onChange={(e) => {
                                        setTagKey(e.target.value)
                                    }}
                                >
                                    {tagKeyOptions.map((key, idx) => (
                                        <MenuItem key={idx} value={key}>{key}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel id="tag-value-select-label">Tag Values</InputLabel>
                                <Select
                                    labelId="tag-value-select-label"
                                    id="tag-value-select"
                                    label="Tag Values"
                                    multiple
                                    value={tagValues}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        setTagValues(Array.isArray(value) ? value : [])
                                    }}
                                    renderValue={(selected) =>
                                        (selected || []).join(', ')
                                    }
                                >
                                    {tagValueOptions.map((val, idx) => (
                                        <MenuItem key={idx} value={val}>{val}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                <Grid
                    container
                    spacing={1}
                    className="add-user-modal-actions"
                >
                    <Grid item xs={12} md={12}>
                        <Box className="add-user-modal-actions-inner">
                            <Button
                                variant='outlined'
                                onClick={closeHandler}
                                className="add-user-modal-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={isSaveDisabled}
                                variant='contained'
                                type="button"
                                onClick={onSaveClick}
                                className="add-user-modal-save-btn"
                            >
                                Save
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    )
}

/**
 * User Management component - Create, Add, Edit and Delete
 * @returns 
 */
export const UserManagement = () => {
    // const [columnList, setColumnList] = useState([{}])
    const [data, setData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [showAddModal, setShowAddModal] = useState(false)
    const [showPermissionModal, setShowPermissionModal] = useState(false)

    const [snackbarMessage, setSnackbarMessage] = useState(null)
    const [snackbarStatus, setSnackbarStatus] = useState(null)
    const [userDataForModal, setUserDataForModal] = useState(null)
    const [permissionUserData, setPermissionUserData] = useState(null)

    const [modalCase, setModalCase] = useState('ADD')
    const [currentUserAccess, setCurrentUserAccess] = useState(null)

    const { getScopedItem } = useScopedLocalStorage()

    const navigate = useNavigate()
    
    const columnList = [
        { key: 'id', label: 'S.no', className: 'num' },
        { key: 'firstname', label: 'First Name' },
        { key: 'lastname', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        {
            key: 'isActive', label: 'Active',
            render: (row) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ToggleButton
                        className="custom-toggle-btn"
                        value={row['isActive'] ? true : false}
                        onToggle={() => onActiveToggleClick(row)}
                    />
                </div>
            ),
        },
        { key: 'emailVerified', label: 'Email Verified', render: (row) => (row['emailVerified'] ? 'Yes' : 'No') },
        { key: 'roleName', label: 'Role' },
        {
            key: 'userId', label: 'Action',
            render: (row) => {
                if (currentUserAccess === USER_ROLE.READ_ONLY) return null;
                return (
                    <div className="user-actions-cell" style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center' }}>
                        <MdEdit
                            color={'#FF9900'}
                            title="Edit"
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                            onClick={() => editUserRequestHandler(row['userId'])}
                        />
                        <MdSecurity
                            title="Manage permissions"
                            color={'#506C84'}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                            onClick={() => {
                                const userId = row['userId'];
                                setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, `${CONST_ROUTE_PATH.SETTINGS.USER_TAG_MANAGEMENT}?user-id=${userId}`);
                                navigate(`${CONST_ROUTE_PATH.SETTINGS.USER_TAG_MANAGEMENT}?user-id=${userId}`);
                            }}
                        />
                    </div>
                );
            },
        },
    ]

    /**
     * Fetch user data
     */
    const fetchData = () => {
        setIsLoading(true)

        UserManagementApiService.getData()
            .then(res => {
                DebugService.log(`User Management Component: API response`, res)
                const apiData = res['data'] ?? []
                setData(apiData.map((d, i) => {
                    return {
                        ...d,
                        id: (i + 1)
                    }
                }))

                if (res['error']) {
                    DebugService.error(`User Management Component: Error caught while fetching data`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`User Management Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })
    }

    /**
     * Call useEffect on page load
     */
    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | User Management`
        fetchData()

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)
        setCurrentUserAccess(parseToJson['roleName'])
    }, [])

    /**
     * API call handler
     * @param {*} endpoint 
     * @param {*} requestBody 
     */
    const saveAPICallHandler = (endpoint, requestBody) => {
        setSnackbarStatus(null)
        setSnackbarMessage(null)
        DebugService.log(`User Management Component: Request to ${modalCase} user initiated`, requestBody)

        UserManagementApiService.saveData(endpoint, requestBody)
            .then(res => {
                DebugService.log(`User Management Component: API response`, res)

                if (res['data']) {
                    setShowAddModal(false)
                    setSnackbarStatus('success')
                    setSnackbarMessage(res['data']['message'])
                    setTimeout(() => {
                        fetchData()
                    }, 1000)
                }

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['message'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.log(`User Management Component: Failed to save data`, err)
                setIsLoading(false)
                setSnackbarStatus('failed')
                setSnackbarMessage('Something went wrong. Please try later')
            })
    }

    /**
     * Add new user handler
     * @param {*} formDetails 
     */
    const onSaveClickHandler = (formDetails) => {
        DebugService.log(`User Management Component: User form`, formDetails, userDataForModal)
        setIsLoading(true)
        setSnackbarMessage(null)
        setSnackbarStatus(null)

        const requestBody = {
            firstname: formDetails['fname'],
            lastname: formDetails['lname'],
            email: formDetails['email'],
            roleName: formDetails['role'],
            accountIds: formDetails['accountIds'] ?? []
        }

        if (formDetails['password']) {
            requestBody['password'] = formDetails['password']
        }

        // Add isActive and userId to request body only when editing a user      
        if (modalCase !== 'ADD') {
            requestBody['isActive'] = userDataForModal['isActive']
            requestBody['userId'] = userDataForModal['userId']
        }

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        // accountIds now come from the selected values in the AddUserModal

        if (parseToJson['ownerId'] && modalCase === 'ADD') {
            requestBody['ownerId'] = parseToJson['ownerId']
        }

        if (parseToJson['userId']) {
            if (modalCase === 'ADD') {
                requestBody['addedBy'] = parseToJson['userId']
            } else {
                // Edit case
                requestBody['updatedBy'] = parseToJson['userId']
            }
        }

        let endpoint = APIConstants.ENDPOINT_ADD_USER

        if (modalCase === 'EDIT') {
            endpoint = APIConstants.ENDPOINT_EDIT_USER
        }

        saveAPICallHandler(endpoint, requestBody)
    }

    /**
     * User edit click handler
     * @param {*} userId 
     */
    const editUserRequestHandler = (userId) => {
        // Disable action for READ ONLY users
        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            return
        }

        DebugService.log(`User Management Component: Selected user id for edit`, userId)
        const userData = data.find(d => d['userId'] === userId)

        if (userData) {
            setModalCase('EDIT')
            setUserDataForModal({
                fname: userData['firstname'],
                lname: userData['lastname'],
                email: userData['email'],
                role: userData['roleName'],
                isActive: userData['isActive'],
                userId: userData['userId'],
                password: userData['password'],
                accountIds: Array.isArray(userData['userAccounts'])
                    ? userData['userAccounts'].map(a => a['accountId'])
                    : []
            })
            setShowAddModal(true)
        }
    }

    /**
     * Active toggle click handler
     * @param {*} userData 
     */
    const onActiveToggleClick = (userData) => {
        // Disable action for READ ONLY users
        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            return
        }

        DebugService.log(`User Management Component: toggle request`, userData)

        setModalCase('EDIT')

        const requestBody = {
            userId: userData['userId'],
            firstname: userData['firstname'],
            lastname: userData['lastname'],
            email: userData['email'],
            roleName: userData['roleName'],
            password: userData['password'],
            updatedBy: '',
            isActive: userData['isActive'] ? 0 : 1
        }

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (parseToJson['userId']) {
            requestBody['updatedBy'] = parseToJson['userId']
        }

        saveAPICallHandler(APIConstants.ENDPOINT_EDIT_USER, requestBody)
    }

    /**
     * Manage permission click handler
     * @param {*} userRow
     */
    const openManagePermissionModal = (userRow) => {
        if (currentUserAccess === USER_ROLE.READ_ONLY) {
            return
        }

        setPermissionUserData(userRow)
        setShowPermissionModal(true)
    }

    return (
        <>
            <div className="breadcrumb">
                Settings <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>User Management</strong>
            </div>

            <div className="page-head">
                <div>
                    <h1>User Management</h1>
                    <div className="meta">Manage users, roles, and account access</div>
                </div>
                <div className="head-actions">
                    {currentUserAccess !== USER_ROLE.READ_ONLY && (
                        <button
                            className="btn primary"
                            title="Add a new user"
                            disabled={isLoading}
                            onClick={() => {
                                setModalCase('ADD')
                                setUserDataForModal(null)
                                setShowAddModal(true)
                            }}
                        >
                            + Add User
                        </button>
                    )}
                    <button className="btn ghost" onClick={fetchData}>↻ Refresh</button>
                </div>
            </div>

            <KpiRow
                kpiData={[
                    { id: 1, type: 't1', title: 'Total Users', value: data.length, subtitle: 'All users' },
                    { id: 2, type: 't2', title: 'Active Users', value: data.filter(u => u['isActive']).length, subtitle: 'Currently active' },
                    { id: 3, type: 't3', title: 'Inactive Users', value: data.filter(u => !u['isActive']).length, subtitle: 'Disabled accounts' },
                ]}
                loading={isLoading}
            />

            <NewTable
                rows={data}
                columns={columnList}
                loading={isLoading}
                skeletonRows={6}
                pageSizes={[10, 25, 50, 100]}
                defaultPageSize={10}
                emptyText="No users found."
            />

            {/* Add/ Edit User Modal */}
            {showAddModal && <AddUserModal
                isClose={(isCloseConfirm) => setShowAddModal(!isCloseConfirm)}
                isOpen={true}
                saveClickHandler={(formDetails) => onSaveClickHandler(formDetails)}
                userCase={modalCase}
                editUserData={userDataForModal}
            />}

            {/* Manage Permission Modal */}
            {showPermissionModal && permissionUserData && (
                <ManagePermissionModal
                    isOpen={showPermissionModal}
                    onClose={() => setShowPermissionModal(false)}
                    user={permissionUserData}
                    tagKeyOptions={[]}
                    tagValueOptions={[]}
                />
            )}

            {/* Snackbar Notification */}
            {snackbarStatus && <SnackbarNotification
                initialOpen={true}
                duration={5000}
                message={snackbarMessage}
                actionButtonName={'Ok'}
                theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
                yPosition={'top'}
                xPosition={'center'}
            />}
        </>
    )
}